import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, ChevronDown, X, Sparkles } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

// ─── Information Architecture (single source of truth) ────────────────────────
//
// Dropdown menus surface pages that were previously hidden in the footer / sub-pages
// (Our Story, Locations, FAQ, all service sub-pages, and the Free IRS Account Review).
//
// Pattern: click-to-open dropdowns, not hover.
//   - Accessible (keyboard navigable, ARIA-compliant)
//   - Touch-device friendly (hover-tap conflicts avoided)
//   - Closes on: outside-click, Escape key, route change, item click
//
// See: /app/docs/SEO_UI_CONSULTATION_LOG.md (SEO-006) for the full IA rationale.

const ABOUT_ITEMS = [
  { label: 'Our Story', to: '/our-story' },
  { label: 'How We Work', to: '/how-we-work' },
  { label: 'Locations', to: '/locations' },
];

const RESOURCES_ITEMS = [
  { label: 'The Journal', to: '/journal', subtext: 'Long-form essays on tax law that moves money' },
  { label: 'Community', to: '/community', subtext: 'Events, resources, and financial education' },
  { label: 'FAQ', to: '/faq', subtext: 'Answers about our services and how we work' },
];

const SERVICES_ITEMS = [
  { label: 'Apply to become a client', to: '/welcome', featured: true, subtext: 'Ongoing accounting, tax, and advisory for your business' },
  { label: 'Ask a CPA', to: '/get-your-answer', featured: true, subtext: 'Get a professional answer to a tax or business question' },
  { label: 'IRS Account Review', to: '/irs-account-review', featured: true, subtext: 'See what the IRS has on file for you' },
  { label: 'Business Health Check', to: '/business-health-check', featured: true, subtext: 'Identify financial, tax, operational, and compliance risks' },
  { label: 'Tax Services', to: '/services/tax', subtext: 'Tax preparation and year-round planning for individuals and businesses' },
  { label: 'Accounting & Payroll', to: '/services/accounting-payroll', subtext: 'Monthly bookkeeping, payroll, and financials you can actually use' },
  { label: 'IRS Relief', to: '/services/irs-relief', subtext: 'A clear path for tax notices, balances, and enforcement concerns' },
  { label: 'Entity Services', to: '/services/entity', subtext: 'Choose and set up the right business structure' },
  { label: 'Business Valuation', to: '/services/valuation', subtext: 'Know what your business is worth and what drives its value' },
  { label: 'Strategic Consulting', to: '/services/consulting', subtext: 'A financially grounded perspective for big decisions' },
  { label: 'Financial Tools', to: '/services/tools', subtext: 'Self-serve calculators, planners, and guides' },
  { divider: true },
  { label: 'View all services', to: '/services' },
];

// ─── Desktop dropdown ─────────────────────────────────────────────────────────

function NavDropdown({ label, items, testId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Close on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 text-sm font-medium text-white/90 hover:text-white transition-colors"
        aria-haspopup="true"
        aria-expanded={open}
        data-testid={testId}
      >
        {label}
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          className="absolute left-1/2 -translate-x-1/2 top-full mt-3 w-80 rounded-md bg-[#F4EFE3] border border-[#E1DFD7] shadow-xl overflow-hidden z-50"
          role="menu"
          data-testid={`${testId}-panel`}
        >
          {/* Top gold hairline */}
          <div className="h-px bg-[#D4AF37]" aria-hidden="true" />
          <div className="py-2">
            {(items || []).map((item, i) => {
              if (item.divider) {
                return <div key={`d-${i}`} className="my-1.5 mx-3 h-px bg-[#0B2240]/10" aria-hidden="true" />;
              }
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className={`block px-4 py-2.5 text-sm transition-colors ${
                    item.featured
                      ? 'text-[#0B2240] font-semibold bg-[#D4AF37]/8 hover:bg-[#D4AF37]/15'
                      : 'text-[#0B2240]/85 font-medium hover:text-[#0B2240] hover:bg-[#0B2240]/5'
                  }`}
                  data-testid={`${testId}-item-${item.to.replace(/[^a-z0-9]/gi, '-')}`}
                  role="menuitem"
                >
                  <span className="flex items-center gap-2">
                    {item.featured && <Sparkles className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0" />}
                    <span>{item.label}</span>
                  </span>
                  {item.subtext && (
                    <span className={`block mt-0.5 text-[11px] leading-snug text-[#0B2240]/55 ${item.featured ? 'ml-5' : ''}`}>
                      {item.subtext}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Mobile accordion item ────────────────────────────────────────────────────

function MobileAccordion({ label, items, onItemClick }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button data-testid="header-button-1"
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-white/90 hover:text-white py-2"
        aria-expanded={open}
      >
        <span className="font-medium">{label}</span>
        <ChevronDown
          className={`w-4 h-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="pl-3 border-l border-[#D4AF37]/40 ml-1 space-y-2 my-2">
          {(items || []).map((item, i) => {
            if (item.divider) {
              return <div key={`d-${i}`} className="h-px bg-white/10 my-1" aria-hidden="true" />;
            }
            return (
              <Link data-testid="header-link-2"
                key={item.to}
                to={item.to}
                onClick={onItemClick}
                className={`block py-1.5 text-sm ${
                  item.featured
                    ? 'text-[#D4AF37] font-semibold'
                    : 'text-white/80 hover:text-white'
                }`}
              >
                <span className="flex items-center gap-2">
                  {item.featured && <Sparkles className="w-3 h-3 flex-shrink-0" />}
                  <span>{item.label}</span>
                </span>
                {item.subtext && (
                  <span className="block mt-0.5 text-[11px] leading-snug text-white/50 font-normal">
                    {item.subtext}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Header ───────────────────────────────────────────────────────────────────

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const closeMobile = () => setMobileMenuOpen(false);
  const location = useLocation();

  // Hide the "Schedule Consultation" CTA on the Ask-a-Question funnel
  // (/get-your-answer/*). On those pages, users are evaluating the PAID
  // Quick Question / Deep Dive / Strategy tiers ($95/$170/$240); the free
  // consult is a direct substitute that cannibalizes paid conversions.
  // Still reachable everywhere else on the site (footer, services pages,
  // /contact, direct URL).
  const isGyaRoute = location.pathname.startsWith('/get-your-answer');

  return (
    <header className="sticky top-0 z-50 w-full border-b border-white/10 bg-[#0B2240]/95 backdrop-blur supports-[backdrop-filter]:bg-[#0B2240]/90">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center group" data-testid="header-logo">
            <img
              src="/brand/kgob-logo-dark.svg"
              alt="Kohari Gonzalez Oneyear & Brown CPAs & Advisors"
              className="h-10 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.03]"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6" style={{ fontFamily: "'Kanit', system-ui, sans-serif" }}>
            <NavDropdown label="About" items={ABOUT_ITEMS} testId="header-about-dropdown" />
            <NavDropdown label="Services" items={SERVICES_ITEMS} testId="header-services-dropdown" />
            <NavDropdown label="Resources" items={RESOURCES_ITEMS} testId="header-resources-dropdown" />
            <Link
              to="/contact"
              className="text-sm font-medium text-white/90 hover:text-white transition-colors"
              data-testid="header-contact-link"
            >
              Contact Us
            </Link>
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-3" style={{ fontFamily: "'Kanit', system-ui, sans-serif" }}>
            <Link
              to="/sign-in"
              className="text-xs font-medium text-white/80 hover:text-white transition-colors"
              data-testid="header-sign-in-link"
            >
              Sign In
            </Link>
            <Button
              asChild
              variant="outline"
              className="h-9 px-4 text-xs rounded-xl border-[#D4AF37]/60 text-[#D4AF37] bg-transparent hover:bg-[#D4AF37]/10 hover:border-[#D4AF37] hover:text-[#D4AF37] focus-visible:ring-2 focus-visible:ring-[#D4AF37]"
              data-testid="header-apply-now-button"
            >
              <a data-testid="header-a-4"
                href="https://app.kgob.com/onboard?source=header-apply-now"
                target="_blank"
                rel="noopener noreferrer"
              >
                Apply Now
              </a>
            </Button>
            {!isGyaRoute && (
              <Button
                asChild
                className="h-9 px-4 text-xs rounded-xl bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240] hover:brightness-95 shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] focus-visible:ring-2 focus-visible:ring-[#1FA3A3]"
                data-testid="header-schedule-button"
              >
                <a data-testid="header-a-3" href="https://app.kgob.com/book-consultation?source=header-schedule">Book a Meeting</a>
              </Button>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            className="lg:hidden p-2 text-white"
            onClick={() => setMobileMenuOpen((o) => !o)}
            data-testid="header-mobile-menu-button"
            aria-expanded={mobileMenuOpen}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 space-y-1 border-t border-white/10" style={{ fontFamily: "'Kanit', system-ui, sans-serif" }} data-testid="header-mobile-menu">
            <MobileAccordion label="About" items={ABOUT_ITEMS} onItemClick={closeMobile} />
            <MobileAccordion label="Services" items={SERVICES_ITEMS} onItemClick={closeMobile} />
            <MobileAccordion label="Resources" items={RESOURCES_ITEMS} onItemClick={closeMobile} />
            <Link data-testid="header-link-1" to="/contact" onClick={closeMobile} className="block text-white/90 hover:text-white py-2 font-medium">
              Contact Us
            </Link>
            <div className="pt-3 mt-2 border-t border-white/10 space-y-2">
              <Link
                to="/sign-in"
                onClick={closeMobile}
                className="block text-center text-white/85 hover:text-white py-1.5 text-sm font-medium"
                data-testid="header-mobile-sign-in-link"
              >
                Sign In
              </Link>
              <Button
                asChild
                variant="outline"
                className="w-full border-[#D4AF37] text-[#D4AF37] bg-transparent hover:bg-[#D4AF37]/10 hover:text-[#D4AF37]"
                data-testid="header-mobile-apply-now"
              >
                <a data-testid="header-a-2"
                  href="https://app.kgob.com/onboard?source=header-apply-now-mobile"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeMobile}
                >
                  Apply Now
                </a>
              </Button>
              {!isGyaRoute && (
                <Button asChild className="w-full bg-[#D4AF37] text-[#0B2240] hover:brightness-95" data-testid="header-mobile-schedule-button">
                  <a data-testid="header-a-1" href="https://app.kgob.com/book-consultation?source=header-schedule-mobile" onClick={closeMobile}>Book a Meeting</a>
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
